/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { forwardRef, useCallback } from "react";
// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Custom styles for the NotistackCustomSnackbar
import MDSnackbarIconRoot from "components/NotistackCustomSnackbar/MDSnackbarIconRoot";

// Material Dashboard 2 PRO React context
import { useMaterialUIController } from "context";
import { SnackbarContent, useSnackbar } from "notistack";

const NotistackCustomSnackbar = forwardRef(({ id, ...props }, ref) => {
  const { title, text, bgWhite = true, ...rest } = JSON.parse(props.message);
  let { color, icon } = rest;
  if (!color) color = icon;
  switch (icon) {
    case "success":
      icon = "checkCircleIcon";
      break;
    case "error":
      icon = "cancelIcon";
      break;
    default:
      break;
  }
  const [controller] = useMaterialUIController();
  const { closeSnackbar } = useSnackbar();
  const { darkMode } = controller;

  let titleColor;
  let dividerColor;

  if (bgWhite) {
    titleColor = color;
    dividerColor = false;
  } else if (color === "light") {
    titleColor = darkMode ? "inherit" : "dark";
    dividerColor = false;
  } else {
    titleColor = "white";
    dividerColor = true;
  }

  const handleDismiss = useCallback(() => {
    closeSnackbar(id);
  }, [id, closeSnackbar]);

  return (
    <SnackbarContent ref={ref} {...rest}>
      <MDBox
        variant={bgWhite ? "contained" : "gradient"}
        bgColor={bgWhite ? "white" : color}
        minWidth="21.875rem"
        maxWidth="100%"
        shadow="md"
        borderRadius="md"
        p={1}
        sx={{
          backgroundColor: ({ palette }) =>
            darkMode ? palette.background.card : palette[color] || palette.white.main,
        }}
      >
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          color="dark"
          p={1.5}
        >
          <MDBox display="flex" alignItems="center" lineHeight={0}>
            <MDSnackbarIconRoot fontSize="small" ownerState={{ color, bgWhite }}>
              {icon}
            </MDSnackbarIconRoot>
            <MDTypography
              variant="button"
              fontWeight="medium"
              color={titleColor}
              textGradient={bgWhite}
            >
              {title}
            </MDTypography>
          </MDBox>
          <MDBox display="flex" alignItems="center" lineHeight={0}>
            <Icon
              sx={{
                color: ({ palette: { dark, white } }) =>
                  (bgWhite && !darkMode) || color === "light" ? dark.main : white.main,
                fontWeight: ({ typography: { fontWeightBold } }) => fontWeightBold,
                cursor: "pointer",
                marginLeft: 2,
                transform: "translateY(-1px)",
              }}
              onClick={handleDismiss}
            >
              close
            </Icon>
          </MDBox>
        </MDBox>
        <Divider sx={{ margin: 0 }} light={dividerColor} />
        <MDBox
          p={1.5}
          sx={{
            fontSize: ({ typography: { size } }) => size.sm,
            color: ({ palette: { white, text: paletteText } }) => {
              let colorValue = bgWhite || color === "light" ? paletteText.main : white.main;

              if (darkMode) {
                colorValue = color === "light" ? "inherit" : white.main;
              }

              return colorValue;
            },
          }}
        >
          {text}
        </MDBox>
      </MDBox>
    </SnackbarContent>
  );
});

// Setting default values for the props of NotistackCustomSnackbar
NotistackCustomSnackbar.defaultProps = {
  bgWhite: false,
  color: "info",
  icon: "",
  title: "",
  text: "",
};

// Typechecking props for NotistackCustomSnackbar
NotistackCustomSnackbar.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
  ]),
  icon: PropTypes.node,
  title: PropTypes.string,
  text: PropTypes.node,
  bgWhite: PropTypes.bool,
};

export default NotistackCustomSnackbar;
